import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React from 'react';
import { Button } from '../../../../../flayr-components/Button/Button';
import { Carousel } from '../../../../../flayr-components/Carousel/Carousel';
import { Flex } from '../../../../../flayr-components/Flex/Flex';
import { GradientText } from '../../../../../flayr-components/GradientText/GradientText';
import { ImgCollage } from '../../../../../flayr-components/ImgCollage/ImgCollage';
import useBackendApi from '../../../../../hooks/useBackendApi';
import useDeviceMediaQuery from '../../../../../hooks/useDeviceMediaQuery';
import useToggle from '../../../../../hooks/useToggle';
import { ImgGalleryModal } from '../../../../../lib/ImgGalleryModal/ImgGalleryModal';
import Portal from '../../../../../lib/Portal/Portal';
import { IMedia } from '../../../../../types/media';

interface IProviderProfileMediaSectionProps {
  media?: IMedia[];
  totalMediaCount?: number;
  backBtnDisabled?: boolean;
  fetchMediaUrl?: string;
  onMediaNext?: (e?: any) => void;
  onGoBack?: (e?: any) => void;
}

const ProviderProfileMediaSection: React.FC<React.PropsWithChildren<IProviderProfileMediaSectionProps>> = ({
  media = [],
  totalMediaCount,
  backBtnDisabled,
  onMediaNext,
  fetchMediaUrl,
  onGoBack,
}) => {
  const galleryModal = useToggle();
  const { isDesktop } = useDeviceMediaQuery();

  const [allMedia, setAllMedia] = React.useState<any[]>([]);
  const { get } = useBackendApi();

  React.useEffect(() => {
    if (galleryModal.opened && !allMedia.length) {
      if (fetchMediaUrl) {
        const fetch = async () => {
          const res = await get(
            fetchMediaUrl,
            {
              limit: 300,
              page: 1,
            },
            { loader: false }
          );

          const media = res.data.docs || [];
          setAllMedia(media);
        };
        fetch();
      } else {
        setAllMedia(media);
      }
    }
  }, [galleryModal.opened, fetchMediaUrl, allMedia, isDesktop]);

  return (
    <>
      <section className="sticky top-0 lg:hidden">
        <div className="relative overflow-hidden w-full h-[100vw]">
          <Flex
            center
            className="absolute top-0 left-0 w-full h-full duration-75 bg-gray-100 bg-opacity-75 animate-pulse"
          >
            <GradientText gradientClassName="bg-primary-to-r text-4xl">
              Flayr
            </GradientText>
          </Flex>
          <Carousel
            className="lg:hidden"
            items={media.map((m) => m.mediaUrl)}
            squareImg
            arrowClassName="text-4xl w-12 h-12 bg-gray-200 flex items-center bg-opacity-50 justify-center rounded shadow-lg backdrop-blur-20"
            totalImages={totalMediaCount}
            onNext={onMediaNext}
            responsiveImage
            screenSizes={{
              200: 300,
              300: 400,
              400: 500,
              600: 700,
              700: 800,
              800: 900,
              900: 800,
              1024: 800,
            }}
          />
          {/* back button */}
          <div hidden={backBtnDisabled} className="absolute top-0 p-4">
            <Button
              color="plain"
              round
              className="w-8 h-8"
              shadow="lg"
              onClick={onGoBack}
            >
              <FontAwesomeIcon
                icon={faChevronLeft}
                className="w-8 h-8 -ml-px leading-none text-primary"
              />
            </Button>
          </div>
          <div className="absolute bottom-0 right-0 pb-8 pr-5">
            <Button
              color="plain"
              shadow="lg"
              rounded="full"
              className="text-primary"
              onClick={galleryModal.toggle}
            >
              <span className="text-sm">View Photos</span>
            </Button>
          </div>
        </div>
      </section>
      <div className="relative w-[100vh] h-screen hidden lg:block">
        <Flex
          center
          className="absolute top-0 left-0 w-full h-full duration-75 bg-gray-100 bg-opacity-75 animate-pulse"
        >
          <GradientText gradientClassName="bg-primary-to-r text-4xl">
            Flayr
          </GradientText>
        </Flex>
        <Carousel
          className="relative "
          items={media.map((m) => m.mediaUrl)}
          squareImg
          arrowClassName="text-4xl w-12 h-12 bg-gray-200 flex items-center bg-opacity-50 justify-center rounded shadow-lg backdrop-blur-20"
          totalImages={totalMediaCount}
          onNext={onMediaNext}
          responsiveImage
          screenSizes={{
            200: 300,
            300: 400,
            400: 500,
            600: 700,
            700: 800,
            800: 900,
            900: 800,
            1024: 800,
          }}
          customArrows={({ prev, next, first, last }) => {
            return (
              <div className="fixed bottom-0 left-0 z-10 flex justify-between w-[100vh] p-8 pl-12">
                <div className="grid grid-cols-2 gap-8">
                  <Button
                    className="w-12 h-12 mr-5"
                    round
                    shadow
                    color="plain"
                    onClick={prev}
                  >
                    <FontAwesomeIcon
                      icon={faChevronLeft}
                      className={classNames('text-2xl', {
                        'text-gray-300': first,
                      })}
                    />
                  </Button>

                  <Button
                    color="plain"
                    className="w-12 h-12"
                    round
                    shadow
                    onClick={next}
                  >
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className={classNames('text-2xl', {
                        'text-gray-300': last,
                      })}
                    />
                  </Button>
                </div>

                {!!media.length && (
                  <div className="ml-auto">
                    <Button
                      color="plain"
                      shadow="lg"
                      size="lg"
                      rounded="lg"
                      onClick={galleryModal.toggle}
                    >
                      See Photos ({totalMediaCount})
                    </Button>
                  </div>
                )}
              </div>
            );
          }}
        />
      </div>

      <Portal>
        <ImgGalleryModal
          images={
            allMedia.map((m) => m.mediaUrl)
            // (isDesktop && allMedia.map((m) => m.mediaUrl)) ||
            // media.map((m) => m.mediaUrl)
          }
          open={galleryModal.opened}
          onClose={galleryModal.toggle}
          zIndex="40"
          onNext={onMediaNext}
          totalImages={totalMediaCount}
          // scrolling={isDesktop}
          scrolling
        />
      </Portal>
    </>
  );
};
export default ProviderProfileMediaSection;
